import { Injectable } from '@angular/core';
import { interval, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { DrupalRESTService } from './drupal-rest.service';
import { map } from 'rxjs/operators';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class AppVersionService {
  // Current application version - update this when deploying new versions
  private appVersion = '1.0.1';
  private versionCheckInterval = 30 * 60 * 1000; // 30 minutes in milliseconds
  private versionCheckSubscription: Subscription | null = null;

  // Notification state management
  private _notificationVisible = new BehaviorSubject<boolean>(false);
  private _serverVersion = new BehaviorSubject<string>('');

  // Public observables
  public notificationVisible$ = this._notificationVisible.asObservable();
  public serverVersion$ = this._serverVersion.asObservable();

  constructor(private _drupalRESTService: DrupalRESTService) {}

  /**
   * Gets the current client-side app version
   */
  getCurrentVersion(): string {
    return this.appVersion;
  }

  /**
   * Gets the latest server-side app version
   */
  getServerVersion(): string {
    return this._serverVersion.getValue();
  }

  /**
   * Starts periodic version check at specified interval
   */
  startPeriodicVersionCheck(): void {
    // Clear any existing subscription
    this.stopPeriodicVersionCheck();

    // Perform an immediate check
    this.checkAppVersion();

    // Create new subscription
    this.versionCheckSubscription = interval(this.versionCheckInterval).subscribe(() => {
      this.checkAppVersion();
    });
  }

  /**
   * Stops periodic version check
   */
  stopPeriodicVersionCheck(): void {
    if (this.versionCheckSubscription) {
      this.versionCheckSubscription.unsubscribe();
      this.versionCheckSubscription = null;
    }
  }

  /**
   * Checks if the current app version matches the server version
   * @returns An observable that emits true if versions match, false otherwise
   */
  checkInitialVersion(serverVersion: string): Observable<boolean> {
    const endpoint = "/app_version";
    const params = [
      { parameter: 'client_version', value: this.appVersion }
    ];

    return this._drupalRESTService.httpGET(endpoint, params).pipe(
      map((response: any) => {
        // Store the server version
        this._serverVersion.next(response.current_version);

        // If server returned a version and it's different from our version
        if (response.requires_update) {
          this.showVersionMismatchNotification();
          return false;
        }
        return true;
      })
    );
  }

  /**
   * Calls the version check endpoint to verify if client is on latest version
   */
  checkAppVersion(): void {
    const endpoint = "/app_version";
    const params = [
      { parameter: 'client_version', value: this.appVersion }
    ];

    this._drupalRESTService.httpGET(endpoint, params).subscribe(
      (response: any) => {
        // Store the server version
        this._serverVersion.next(response.current_version);

        if (response.requires_update) {
          this.showVersionMismatchNotification();
        }
      },
      error => {
        LoggerService.error("Error checking app version:", error);
      }
    );
  }

  /**
   * Shows a non-blocking notification when a version mismatch is detected
   */
  private showVersionMismatchNotification(): void {
    this._notificationVisible.next(true);
  }

  /**
   * Hides the version mismatch notification
   */
  public hideVersionMismatchNotification(): void {
    this._notificationVisible.next(false);
  }

  /**
   * Refreshes the page to update to the latest version
   */
  public refreshPage(): void {
    window.location.reload();
  }
}
